import { useEffect, useRef, useState } from 'react';

export const AudioController = () => {
  const announcementRef = useRef(null);
  const backgroundMusicRef = useRef(null);
  const [hasInteracted, setHasInteracted] = useState(false);

  const playAudioSequence = () => {
    console.log('=== Starting audio sequence ===');
    
    if (!announcementRef.current || !backgroundMusicRef.current) {
      console.error('Audio elements not found');
      return;
    }

    const announcement = announcementRef.current;
    const background = backgroundMusicRef.current;

    // Set volume levels (0.0 to 1.0)
    announcement.volume = 0.5; // 50% volume for announcement
    background.volume = 0.3; // 30% volume for background music (quieter)

    console.log('Announcement audio element:', announcement);
    console.log('Background audio element:', background);
    console.log('Announcement src:', announcement.src);
    console.log('Background src:', background.src);

    // Play the announcement
    console.log('Attempting to play announcement...');
    const playPromise = announcement.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log('✅ Announcement playing successfully');
          console.log('Announcement duration:', announcement.duration);
          
          // Set up the background music to start halfway through
          const startBackgroundMusic = () => {
            const duration = announcement.duration;
            if (!isNaN(duration) && duration > 0) {
              const halfwayPoint = duration / 2;
              console.log(`⏱️ Will start background music in ${halfwayPoint} seconds`);
              
              setTimeout(() => {
                console.log('Starting background music NOW...');
                background.play()
                  .then(() => console.log('✅ Background music playing successfully'))
                  .catch(err => console.error('❌ Background music error:', err));
              }, halfwayPoint * 1000);
            } else {
              console.log('Duration not ready, waiting for canplay event...');
              announcement.addEventListener('canplay', () => {
                const halfwayPoint = announcement.duration / 2;
                console.log(`⏱️ Will start background music in ${halfwayPoint} seconds (from canplay)`);
                
                setTimeout(() => {
                  console.log('Starting background music NOW...');
                  background.play()
                    .then(() => console.log('✅ Background music playing successfully'))
                    .catch(err => console.error('❌ Background music error:', err));
                }, halfwayPoint * 1000);
              }, { once: true });
            }
          };

          // Try to start background music timing
          if (announcement.duration && !isNaN(announcement.duration)) {
            startBackgroundMusic();
          } else {
            announcement.addEventListener('loadedmetadata', startBackgroundMusic, { once: true });
          }
        })
        .catch(err => {
          console.error('❌ Announcement playback error:', err);
          alert(`Audio playback failed: ${err.message}\n\nPlease ensure your browser allows audio playback.`);
        });
    }
  };

  const handleClick = () => {
    console.log('🖱️ Button clicked');
    setHasInteracted(true);
    // Small delay to ensure state updates before playing
    setTimeout(playAudioSequence, 100);
  };

  return (
    <>
      {/* Stalin death announcement - plays once */}
      <audio 
        ref={announcementRef}
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="/audio/announcement.m4a" type="audio/mp4" />
        Your browser does not support the audio element.
      </audio>

      {/* Aquarium - Fishermen discussing the sunset - loops */}
      <audio 
        ref={backgroundMusicRef}
        loop
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="/audio/background.m4a" type="audio/mp4" />
        Your browser does not support the audio element.
      </audio>

      {/* Striking animated prompt - Y2K cyber style */}
      {!hasInteracted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/98 backdrop-blur-md crt-scanlines wire-grid">
          <div className="text-center">
            {/* Glitch text header */}
            <div className="mb-8">
              <p className="text-sm digital-text text-bondi-blue/70 mb-4 tracking-widest">
                [SYSTEM INITIALIZATION]
              </p>
              <div className="relative">
                <div 
                  className="inline-block px-16 py-8 border-2 border-bondi-blue bg-background relative cursor-pointer group hover:bg-bondi-blue/10 transition-all duration-300"
                  onClick={handleClick}
                  style={{
                    boxShadow: '0 0 20px rgba(0, 149, 182, 0.4), inset 0 0 20px rgba(0, 149, 182, 0.1)'
                  }}
                >
                  {/* Corner decorations */}
                  <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary"></div>
                  <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary"></div>
                  <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary"></div>
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary"></div>
                  
                  <p className="text-3xl md:text-4xl font-bold text-bondi-blue tracking-wider digital-text mb-2 group-hover:text-shadow-glow">
                    &gt;&gt; CLICK TO BEGIN &lt;&lt;
                  </p>
                  <p className="text-xs text-bondi-blue/60 digital-text tracking-[0.3em]">
                    PRESS ANY KEY TO CONTINUE
                  </p>
                </div>
                
                {/* Animated corner brackets */}
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-bondi-blue/40 text-xl animate-pulse">
                  ▼
                </div>
              </div>
            </div>
            
            {/* Status text */}
            <div className="mt-12 space-y-2">
              <p className="text-xs digital-text text-bondi-blue/50 tracking-wider">
                <span className="animate-pulse">▋</span> AWAITING USER INPUT...
              </p>
              <p className="text-[10px] digital-text text-bondi-blue/30 tracking-widest">
                PROTOCOL://CARBON.PAGES v1.998
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
